/* eslint-disable max-lines */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  Collapse,
  IconButton,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import {
  CheckCircle as CompletedIcon,
  Schedule as ProcessingIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Pause as PendingIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Assessment as ReportIcon,
  Build as ToolIcon,
  Code as CodeIcon,
} from '@mui/icons-material';

// Status configurations
const STATUS_CONFIG = {
  completed: {
    icon: CompletedIcon,
    color: 'success',
    label: 'Completed',
    bgColor: '#e8f5e8'
  },
  processing: {
    icon: ProcessingIcon,
    color: 'info',
    label: 'Processing',
    bgColor: '#e3f2fd'
  },
  error: {
    icon: ErrorIcon,
    color: 'error',
    label: 'Failed',
    bgColor: '#ffebee'
  },
  insufficient_data: {
    icon: WarningIcon,
    color: 'warning',
    label: 'No Data',
    bgColor: '#fff3e0'
  },
  pending: {
    icon: PendingIcon,
    color: 'default',
    label: 'Pending',
    bgColor: '#f5f5f5'
  }
};

const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.pending;

// eslint-disable-next-line max-lines-per-function
const VariableProcessingDisplay = ({
  reportStatus,
  variables = [],
  totalVariables = 0,
  processedVariables = 0,
  errorCount = 0,
  insufficientDataCount = 0,
  _loading = false
}) => {
  const [showAllVariables, setShowAllVariables] = useState(false);
  const [showIssuesDetail, setShowIssuesDetail] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState(null);
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [promptTabValue, setPromptTabValue] = useState(0);

  // Calculate progress
  const progressPercentage = totalVariables > 0 ? Math.round((processedVariables / totalVariables) * 100) : 0;

  // Get status configuration
  const getStatusDisplay = (status) => {
    if (!status) return 'Initializing';

    const statusMap = {
      'processing_variables': 'Processing Variables',
      'all_variables_processed': 'All Variables Processed',
      'completed': 'Completed',
      'error': 'Error'
    };
    return statusMap[status] || status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    if (status === 'processing_variables') return 'info';
    if (status === 'all_variables_processed' || status === 'completed') return 'success';
    if (status === 'error') return 'error';
    return 'default';
  };

  // Calculate status counts
  const statusCounts = {
    completed: variables.filter(v => v.status === 'completed').length,
    processing: variables.filter(v => v.status === 'processing').length,
    error: variables.filter(v => v.status === 'error').length,
    insufficient_data: variables.filter(v => v.status === 'insufficient_data').length,
    pending: variables.filter(v => !v.status || v.status === 'pending').length
  };

  // Filter variables for display
  const getDisplayVariables = () => {
    if (showAllVariables || variables.length <= 15) {
      return variables;
    }

    // Show: all processing, all errors, and recent completions
    const processing = variables.filter(v => v.status === 'processing');
    const errors = variables.filter(v => v.status === 'error' || v.status === 'insufficient_data');
    const completed = variables.filter(v => v.status === 'completed');
    const pending = variables.filter(v => !v.status || v.status === 'pending');

    return [
      ...processing,
      ...errors,
      ...completed.slice(-5),
      ...pending.slice(0, 3)
    ];
  };

  const displayVariables = getDisplayVariables();
  const processingVars = variables.filter(v => v.status === 'processing');
  const hasIssues = errorCount > 0 || insufficientDataCount > 0;

  // Handle opening prompt dialog
  const handleViewPrompt = (variable) => {
    setSelectedVariable(variable);
    setPromptDialogOpen(true);
    setPromptTabValue(0);
  };

  // Handle closing prompt dialog
  const handleClosePromptDialog = () => {
    setPromptDialogOpen(false);
    setSelectedVariable(null);
    setPromptTabValue(0);
  };

  // Check if report is completed
  const isCompleted = reportStatus === 'completed' ||
                     reportStatus === 'all_variables_processed' ||
                     (totalVariables > 0 && processedVariables >= totalVariables);

  // Show full display if we have any meaningful data
  const hasData = totalVariables > 0 || variables.length > 0 || processedVariables > 0;

  // Show loading state only if no meaningful data is available AND not completed
  if (!hasData && !isCompleted) {
    return (
      <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <ReportIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h3">
            Report Generation Progress
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            <Chip
              label={getStatusDisplay(reportStatus)}
              color={getStatusColor(reportStatus)}
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Loading State */}
        <Card sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ProcessingIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6">
                Initializing Report Generation...
              </Typography>
            </Box>
            <LinearProgress sx={{ mb: 2, height: 8, borderRadius: 4 }} />
            <Typography variant="body2" color="text.secondary">
              Setting up variables and preparing analysis tools. This may take a moment.
            </Typography>
          </CardContent>
        </Card>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ReportIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" component="h3">
          Report Generation Progress
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <Chip
            label={getStatusDisplay(reportStatus)}
            color={getStatusColor(reportStatus)}
            variant="outlined"
          />
        </Box>
      </Box>
      {/* Progress Section */}
      <Card sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Overall Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {processedVariables}/{totalVariables} variables ({progressPercentage}%)
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          <Grid container spacing={2}>
            {Object.entries(statusCounts).map(([status, count]) => {
              const config = getStatusConfig(status);
              const IconComponent = config.icon;
              return (
                <Grid
                  key={status}
                  size={{
                    xs: 6,
                    sm: 2.4
                  }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Badge badgeContent={count} color={config.color} max={999}>
                      <IconComponent sx={{ color: `${config.color}.main`, fontSize: 28 }} />
                    </Badge>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      {config.label}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
      {/* Issues Alert */}
      {hasIssues && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setShowIssuesDetail(!showIssuesDetail)}
            >
              {showIssuesDetail ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        >
          <Typography variant="body2">
            Issues detected: {errorCount} errors, {insufficientDataCount} insufficient data
          </Typography>
          <Collapse in={showIssuesDetail}>
            <Box sx={{ mt: 1 }}>
              {errorCount > 0 && (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <ErrorIcon sx={{ mr: 1, fontSize: 16 }} />
                  {errorCount} variables failed to process
                </Typography>
              )}
              {insufficientDataCount > 0 && (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  <WarningIcon sx={{ mr: 1, fontSize: 16 }} />
                  {insufficientDataCount} variables have insufficient data
                </Typography>
              )}
            </Box>
          </Collapse>
        </Alert>
      )}
      {/* Currently Processing */}
      {processingVars.length > 0 && (
        <Card sx={{ mb: 3, backgroundColor: '#e3f2fd' }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ProcessingIcon sx={{ mr: 1 }} />
              Currently Processing ({processingVars.length} in parallel)
            </Typography>
            <List dense>
              {processingVars.map((variable, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ToolIcon sx={{ fontSize: 16 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={variable.variableName}
                    secondary={variable.toolCallCount ? `${variable.toolCallCount} tool calls` : 'Starting...'}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
      {/* Variable Details Table */}
      {variables.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">
              Variable Details
            </Typography>
            {variables.length > 15 && (
              <IconButton
                onClick={() => setShowAllVariables(!showAllVariables)}
                size="small"
              >
                {showAllVariables ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                <Typography variant="caption" sx={{ ml: 0.5 }}>
                  {showAllVariables ? 'Show Less' : `Show All (${variables.length})`}
                </Typography>
              </IconButton>
            )}
          </Box>

          {!showAllVariables && variables.length > 15 && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Showing {displayVariables.length} of {variables.length} variables (focused view)
            </Typography>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Variable</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell align="center">Tools Used</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayVariables.map((variable, index) => {
                  const config = getStatusConfig(variable.status);
                  const IconComponent = config.icon;

                  let progressInfo = '-';
                  if (variable.status === 'processing') {
                    progressInfo = `${variable.toolCallCount || 0} tool calls`;
                  } else if (variable.status === 'completed') {
                    progressInfo = 'Complete';
                  } else if (variable.status === 'error') {
                    progressInfo = 'Failed';
                  } else if (variable.status === 'insufficient_data') {
                    progressInfo = 'No data available';
                  }

                  return (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: config.bgColor,
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                    >
                      <TableCell>
                        <Chip
                          icon={<IconComponent />}
                          label={config.label}
                          size="small"
                          color={config.color}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {variable.variableName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {progressInfo}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {variable.mcpToolsUsed && variable.mcpToolsUsed.length > 0 ? (
                          <Chip
                            label={variable.mcpToolsUsed.length}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {(variable.systemPrompt || variable.userPrompt || variable.completePrompt) ? (
                          <IconButton
                            size="small"
                            onClick={() => handleViewPrompt(variable)}
                            title="View Prompt"
                          >
                            <CodeIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {/* Prompt Dialog */}
      <Dialog
        open={promptDialogOpen}
        onClose={handleClosePromptDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Prompt Details: {selectedVariable?.variableName}
            </Typography>
            <IconButton
              onClick={handleClosePromptDialog}
              size="small"
            >
              <ExpandLessIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={promptTabValue}
              onChange={(e, newValue) => setPromptTabValue(newValue)}
              aria-label="prompt tabs"
            >
              <Tab label="Complete Prompt" />
              <Tab label="System Prompt" />
              <Tab label="User Prompt" />
              <Tab label="Variable Info" />
            </Tabs>
          </Box>

          {/* Complete Prompt Tab */}
          {promptTabValue === 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Complete Prompt as Sent to LLM
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'grey.100',
                  maxHeight: '60vh',
                  overflow: 'auto',
                  fontFamily: 'monospace'
                }}
              >
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'inherit',
                    fontSize: '0.875rem',
                    lineHeight: 1.4
                  }}
                >
                  {selectedVariable?.completePrompt || 'Prompt information not available for this variable.'}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* System Prompt Tab */}
          {promptTabValue === 1 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                System Prompt
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'grey.100',
                  maxHeight: '60vh',
                  overflow: 'auto',
                  fontFamily: 'monospace'
                }}
              >
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'inherit',
                    fontSize: '0.875rem',
                    lineHeight: 1.4
                  }}
                >
                  {selectedVariable?.systemPrompt || 'System prompt not available for this variable.'}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* User Prompt Tab */}
          {promptTabValue === 2 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                User Prompt
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'grey.100',
                  maxHeight: '60vh',
                  overflow: 'auto',
                  fontFamily: 'monospace'
                }}
              >
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'inherit',
                    fontSize: '0.875rem',
                    lineHeight: 1.4
                  }}
                >
                  {selectedVariable?.userPrompt || 'User prompt not available for this variable.'}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Variable Info Tab */}
          {promptTabValue === 3 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Variable Information
              </Typography>
              <Paper sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <Typography variant="body2" color="text.secondary">
                      Variable Name:
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {selectedVariable?.variableName}
                    </Typography>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <Typography variant="body2" color="text.secondary">
                      Status:
                    </Typography>
                    <Chip
                      label={getStatusConfig(selectedVariable?.status).label}
                      color={getStatusConfig(selectedVariable?.status).color}
                      size="small"
                    />
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="body2" color="text.secondary">
                      Description:
                    </Typography>
                    <Typography variant="body1">
                      {selectedVariable?.variableDescription}
                    </Typography>
                  </Grid>
                  {selectedVariable?.mcpToolsUsed && selectedVariable.mcpToolsUsed.length > 0 && (
                    <Grid size={12}>
                      <Typography variant="body2" color="text.secondary">
                        MCP Tools Used:
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        {selectedVariable.mcpToolsUsed.map((tool, index) => (
                          <Chip
                            key={index}
                            label={tool}
                            size="small"
                            variant="outlined"
                            sx={{ mr: 1, mb: 1 }}
                          />
                        ))}
                      </Box>
                    </Grid>
                  )}
                  {selectedVariable?.result && (
                    <Grid size={12}>
                      <Typography variant="body2" color="text.secondary">
                        Result:
                      </Typography>
                      <Paper sx={{ p: 1, bgcolor: 'grey.50', mt: 1 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedVariable.result}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  {selectedVariable?.error && (
                    <Grid size={12}>
                      <Typography variant="body2" color="error">
                        Error:
                      </Typography>
                      <Paper sx={{ p: 1, bgcolor: 'error.light', mt: 1 }}>
                        <Typography variant="body2" color="error.contrastText">
                          {selectedVariable.error}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePromptDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default VariableProcessingDisplay;